import { apiFetch } from '../../auth/AuthContext';
/**
 * =========================================================
 *  TEAMMATE 2 — DELETE TAB  (Data Ingestion Monitor)
 *  Queries ClickHouse directly via /ch proxy → localhost:8123
 *  All styles scoped under dc-* prefix — no impact on other tabs.
 * =========================================================
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Trade } from '../../types/Trade';
import { useCoinRegistry } from '../../shared/CoinRegistryContext';

interface DeleteTabProps {
  trades: Trade[];
  onDelete: (id: string) => void;
}

// ── ClickHouse HTTP helper ────────────────────────────────────────────────────
// Credentials are injected server-side by the Nginx /ch proxy.
async function chQuery<T = Record<string, unknown>>(sql: string): Promise<T[]> {
  const r = await apiFetch('/ch', {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: sql.trim() + '\nFORMAT JSON',
  });
  if (!r.ok) {
    const txt = await r.text();
    throw new Error(`CH error ${r.status}: ${txt.slice(0, 200)}`);
  }
  const j = await r.json();
  return j.data as T[];
}

// ── Scoped styles ──────────────────────────────────────────────────────────────
const DC_STYLES = `
.dc-wrap{display:flex;flex-direction:column;height:calc(100vh - 100px);background:#0a0e14;color:#c5cdd8;font-family:'SF Mono','Cascadia Code','Fira Code','Consolas',monospace;font-size:12px;line-height:1.4;overflow:hidden}
.dc-header{display:flex;align-items:center;justify-content:space-between;padding:8px 16px;border-bottom:1px solid #1a2332;background:#111820;flex-shrink:0}
.dc-header-left{display:flex;align-items:center;gap:14px}
.dc-title{font-size:14px;font-weight:700;color:#e8edf3;letter-spacing:-0.3px}
.dc-badge{display:inline-flex;align-items:center;gap:4px;padding:2px 10px;border-radius:4px;font-size:11px;font-weight:600}
.dc-badge-cyan{background:rgba(38,198,218,.15);color:#26c6da}
.dc-badge-green{background:rgba(0,230,118,.15);color:#00e676}
.dc-badge-yellow{background:rgba(240,185,11,.15);color:#f0b90b}
.dc-header-right{display:flex;align-items:center;gap:6px}
.dc-tab-bar{display:flex;gap:2px}
.dc-tab-btn{background:transparent;border:1px solid #1a2332;color:#5a6a7a;padding:4px 14px;border-radius:4px;cursor:pointer;font-size:11px;font-weight:600;font-family:inherit;transition:all .15s}
.dc-tab-btn:hover{background:#111820;color:#c5cdd8}
.dc-tab-btn.dc-active{background:#00e676;color:#000;border-color:#00e676}
.dc-ctrl-group{display:flex;gap:0;margin-left:8px}
.dc-ctrl-btn{background:#111820;border:1px solid #1a2332;color:#5a6a7a;padding:4px 10px;font-size:11px;cursor:pointer;font-family:inherit;transition:all .12s}
.dc-ctrl-btn:first-child{border-radius:4px 0 0 4px}
.dc-ctrl-btn:last-child{border-radius:0 4px 4px 0}
.dc-ctrl-btn:hover{color:#c5cdd8;background:#1a2332}
.dc-ctrl-btn.dc-active{color:#e8edf3;background:#243040;border-color:#243040}
.dc-ctrl-label{color:#5a6a7a;font-size:10px;margin-right:4px}
.dc-cal-trigger{background:#111820;border:1px solid #1a2332;color:#c5cdd8;padding:3px 8px;border-radius:4px;font-size:10px;font-family:inherit;cursor:pointer;transition:all .12s;display:inline-flex;align-items:center;gap:5px;white-space:nowrap;user-select:none}
.dc-cal-trigger:hover{color:#e8edf3;border-color:#243040;background:#1a2332}
.dc-cal-trigger.dc-open{border-color:#26c6da;color:#26c6da}
.dc-cal-trigger svg{width:10px;height:10px;opacity:.5;fill:currentColor}
.dc-cal-wrap{position:relative;display:inline-flex;align-items:center}
.dc-cal-drop{position:absolute;top:calc(100% + 6px);right:0;z-index:100;background:#111820;border:1px solid #1a2332;border-radius:8px;box-shadow:0 8px 32px rgba(0,0,0,.6);padding:10px;min-width:240px;animation:dc-fade-in .12s ease-out}
@keyframes dc-fade-in{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}
.dc-cal-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:8px}
.dc-cal-title{font-size:12px;font-weight:700;color:#e8edf3;letter-spacing:-0.2px}
.dc-cal-nav{background:transparent;border:1px solid #1a2332;color:#5a6a7a;width:22px;height:22px;border-radius:4px;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:11px;font-family:inherit;transition:all .12s;padding:0}
.dc-cal-nav:hover{color:#e8edf3;background:#1a2332;border-color:#243040}
.dc-cal-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:1px}
.dc-cal-dow{text-align:center;font-size:9px;color:#5a6a7a;padding:2px 0 4px;font-weight:600;text-transform:uppercase}
.dc-cal-day{text-align:center;font-size:11px;padding:4px 2px;border-radius:3px;cursor:pointer;transition:all .1s;color:#c5cdd8;border:1px solid transparent}
.dc-cal-day:hover{background:#1a2332;color:#e8edf3}
.dc-cal-day.dc-other{color:#2a3a50}
.dc-cal-day.dc-today{border-color:#26c6da44;color:#26c6da}
.dc-cal-day.dc-selected{background:#26c6da;color:#0a0e14;font-weight:700}
.dc-cal-day.dc-in-range{background:rgba(38,198,218,.1);color:#26c6da}
.dc-cal-day.dc-range-start{background:#26c6da;color:#0a0e14;font-weight:700;border-radius:3px 0 0 3px}
.dc-cal-day.dc-range-end{background:#26c6da;color:#0a0e14;font-weight:700;border-radius:0 3px 3px 0}
.dc-cal-day.dc-range-start.dc-range-end{border-radius:3px}
.dc-cal-backdrop{position:fixed;inset:0;z-index:99}
.dc-main{display:flex;flex:1;overflow:hidden}
.dc-sidebar{width:105px;flex-shrink:0;padding:12px 10px;border-right:1px solid #1a2332;background:#111820;overflow-y:auto;font-size:10px}
.dc-sidebar-section{margin-bottom:14px}
.dc-sidebar-label{color:#5a6a7a;text-transform:uppercase;font-size:9px;letter-spacing:0.5px;margin-bottom:4px}
.dc-sidebar-value{color:#26c6da;font-size:20px;font-weight:700;margin-bottom:2px}
.dc-sidebar-sub{color:#5a6a7a;font-size:10px;margin-bottom:1px}
.dc-sidebar-sub span{color:#c5cdd8}
.dc-legend-item{display:flex;align-items:center;gap:5px;margin-bottom:3px;font-size:10px}
.dc-legend-dot{width:8px;height:8px;border-radius:2px;flex-shrink:0}
.dc-heatmap-container{flex:1;overflow:auto}
.dc-heatmap-table{border-collapse:collapse}
.dc-heatmap-table th{position:sticky;top:0;z-index:10;background:#111820;border-bottom:1px solid #1a2332;padding:4px 1px;font-size:9px;color:#5a6a7a;font-weight:500;white-space:nowrap;text-align:center;min-width:22px}
.dc-heatmap-table th.dc-sticky-col{position:sticky;z-index:21;background:#111820}
.dc-category-row td{padding:8px 6px 3px;color:#00e676;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;border:none;background:#0a0e14}
.dc-coin-row td{padding:1px 2px;border-bottom:1px solid rgba(26,35,50,.4);font-size:11px;white-space:nowrap;background:#0a0e14}
.dc-coin-row:hover td{background:rgba(38,198,218,.03)}
.dc-coin-row td.dc-sticky-col{position:sticky;z-index:20;background:#0a0e14}
.dc-coin-row:hover td.dc-sticky-col{background:rgba(38,198,218,.03)}
.dc-col-status{width:16px;left:0}
.dc-col-coin{left:16px;min-width:65px;padding-left:2px !important}
.dc-col-price{left:81px;min-width:70px;text-align:right !important}
.dc-col-chg{left:151px;min-width:55px;text-align:right !important}
.dc-col-cov{left:206px;min-width:40px;text-align:right !important}
.dc-status-dot{width:6px;height:6px;border-radius:50%;display:inline-block}
.dc-status-dot.dc-online{background:#00e676}
.dc-status-dot.dc-partial{background:#ffa726}
.dc-status-dot.dc-offline{background:#ef5350}
.dc-coin-name{color:#e8edf3;font-weight:600}
.dc-hm-cell{padding:1px 0 !important;text-align:center}
.dc-hm-block{display:inline-block;width:20px;height:14px;border-radius:1px;vertical-align:middle}
.dc-hm-full{background:#00e676;opacity:0.85}
.dc-hm-high{background:#00e676;opacity:0.6}
.dc-hm-mid{background:#00e676;opacity:0.4}
.dc-hm-low{background:#00e676;opacity:0.2}
.dc-hm-empty{background:#1a2332;opacity:0.3}
.dc-hm-block:hover{opacity:1 !important;filter:brightness(1.3)}
.dc-loading{display:flex;align-items:center;justify-content:center;padding:60px;color:#5a6a7a;gap:10px}
.dc-spinner{width:16px;height:16px;border:2px solid #1a2332;border-top-color:#26c6da;border-radius:50%;animation:dc-spin .6s linear infinite}
@keyframes dc-spin{to{transform:rotate(360deg)}}
.dc-pane{padding:20px;flex:1;overflow:auto}
.dc-cards{display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:12px;margin-bottom:20px}
.dc-card{background:#111820;border:1px solid #1a2332;border-radius:8px;padding:14px 16px}
.dc-card-label{font-size:9px;color:#5a6a7a;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px}
.dc-card-value{font-size:20px;font-weight:700}
.dc-section-title{font-size:14px;font-weight:700;color:#e8edf3;margin:16px 0 10px}
table.dc-data-table{width:100%;border-collapse:collapse;font-size:11px;background:#111820;border:1px solid #1a2332;border-radius:8px;overflow:hidden}
table.dc-data-table th{text-align:left;padding:8px 10px;border-bottom:2px solid #1a2332;color:#5a6a7a;font-size:10px;text-transform:uppercase;font-weight:600}
table.dc-data-table td{padding:6px 10px;border-bottom:1px solid #1a2332}
table.dc-data-table tr:hover td{background:rgba(38,198,218,.03)}
.dc-num{text-align:right}
select.dc-ctrl-select{background:#111820;border:1px solid #1a2332;color:#c5cdd8;padding:4px 10px;border-radius:4px;font-size:11px;font-family:inherit;cursor:pointer}
.dc-controls-row{display:flex;gap:8px;align-items:center;margin-bottom:16px;flex-wrap:wrap}
.dc-filter-sep{width:1px;height:18px;background:#1a2332;margin:0 6px;flex-shrink:0}
`;

// ── Types ──────────────────────────────────────────────────────────────────────
interface HeatmapSlot {
  hour: string;
  ohlcv_count: number;
  features: string[];
}
interface HeatmapCoin {
  symbol: string;
  slots: HeatmapSlot[];
}

interface SummaryInfo {
  spotRows: number;
  futuresRows: number;
  spot1mRows: number;
  futures1mRows: number;
  disk: Record<string, number>;
  minTime: string;
  maxTime: string;
  coins: {
    symbol: string;
    spotRows: number;
    futuresRows: number;
    spotFrom: string;
    spotTo: string;
  }[];
  featureCoverage: Record<string, Record<string, number>>;
  preprocessed: Record<string, Record<string, number>>;
}

// ── Coin categories ────────────────────────────────────────────────────────────
const CATEGORIES: Record<string, string[]> = {
  'LAYER 1': [
    'BTCUSDT',
    'ETHUSDT',
    'SOLUSDT',
    'BNBUSDT',
    'XRPUSDT',
    'ADAUSDT',
    'AVAXUSDT',
    'DOTUSDT',
  ],
  DEFI: ['LINKUSDT', 'AAVEUSDT', 'UNIUSDT', 'PENDLEUSDT', 'LDOUSDT', 'ENAUSDT'],
  'AI AGENT': [
    'RENDERUSDT',
    'FETUSDT',
    'WLDUSDT',
    'ICPUSDT',
    'GRTUSDT',
    'TAOUSDT',
  ],
  'LAYER 2': ['ARBUSDT', 'OPUSDT', 'STXUSDT', 'IMXUSDT', 'MANTAUSDT'],
  LEGACY: ['LTCUSDT', 'BCHUSDT', 'XLMUSDT', 'ETCUSDT', 'TRXUSDT', 'DOGEUSDT'],
};
const ALL_FEATURES = [
  'open_interest',
  'global_ls_ratio',
  'top_ls_account',
  'top_ls_position',
  'taker_buy_sell',
  'funding_rate',
  'spot_depth',
  'futures_depth',
];

// ── Calendar date-range picker ────────────────────────────────────────────────
const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const DOW = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function DateRangeCal({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => parseInt(from.slice(0, 4)));
  const [viewMonth, setViewMonth] = useState(
    () => parseInt(from.slice(5, 7)) - 1,
  );
  const [picking, setPicking] = useState<'from' | 'to' | null>(null);
  const [tempFrom, setTempFrom] = useState(from);
  const [tempTo, setTempTo] = useState(to);

  const openCal = () => {
    setTempFrom(from);
    setTempTo(to);
    setPicking(null);
    const y = parseInt(from.slice(0, 4));
    const m = parseInt(from.slice(5, 7)) - 1;
    setViewYear(y);
    setViewMonth(m);
    setOpen(true);
  };

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else setViewMonth(viewMonth + 1);
  };

  const pad2 = (n: number) => String(n).padStart(2, '0');
  const toStr = (y: number, m: number, d: number) =>
    `${y}-${pad2(m + 1)}-${pad2(d)}`;
  const todayS = todayStr();

  // Build calendar grid
  const firstDay = new Date(viewYear, viewMonth, 1).getDay(); // 0=Sun
  const offset = firstDay === 0 ? 6 : firstDay - 1; // Mon-based
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const prevDays = new Date(viewYear, viewMonth, 0).getDate();

  const cells: { day: number; month: number; year: number; other: boolean }[] =
    [];
  for (let i = 0; i < offset; i++) {
    const d = prevDays - offset + 1 + i;
    const pm = viewMonth === 0 ? 11 : viewMonth - 1;
    const py = viewMonth === 0 ? viewYear - 1 : viewYear;
    cells.push({ day: d, month: pm, year: py, other: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, month: viewMonth, year: viewYear, other: false });
  }
  const remaining = 7 - (cells.length % 7);
  if (remaining < 7) {
    for (let i = 1; i <= remaining; i++) {
      const nm = viewMonth === 11 ? 0 : viewMonth + 1;
      const ny = viewMonth === 11 ? viewYear + 1 : viewYear;
      cells.push({ day: i, month: nm, year: ny, other: true });
    }
  }

  const handleDayClick = (cell: (typeof cells)[0]) => {
    const ds = toStr(cell.year, cell.month, cell.day);
    if (!picking || picking === 'from') {
      setTempFrom(ds);
      setTempTo(ds);
      setPicking('to');
    } else {
      if (ds < tempFrom) {
        setTempFrom(ds);
        setPicking('to');
      } else {
        setTempTo(ds);
        setPicking(null);
        onChange(tempFrom, ds);
        setOpen(false);
      }
    }
  };

  const dayClass = (cell: (typeof cells)[0]) => {
    const ds = toStr(cell.year, cell.month, cell.day);
    const cls = ['dc-cal-day'];
    if (cell.other) cls.push('dc-other');
    if (ds === todayS) cls.push('dc-today');
    const f = picking ? tempFrom : from;
    const t = picking ? tempTo : to;
    if (ds === f && ds === t) cls.push('dc-selected');
    else if (ds === f) cls.push('dc-range-start');
    else if (ds === t) cls.push('dc-range-end');
    else if (ds > f && ds < t) cls.push('dc-in-range');
    return cls.join(' ');
  };

  const fmtDisplay = (d: string) => {
    const parts = d.split('-');
    return `${MONTH_NAMES[parseInt(parts[1]) - 1]} ${parseInt(parts[2])}`;
  };

  return (
    <div className="dc-cal-wrap">
      <button
        className={`dc-cal-trigger${open ? ' dc-open' : ''}`}
        onClick={openCal}
      >
        <svg viewBox="0 0 16 16" fill="currentColor">
          <path d="M4 0v1H1.5A1.5 1.5 0 000 2.5v12A1.5 1.5 0 001.5 16h13a1.5 1.5 0 001.5-1.5v-12A1.5 1.5 0 0014.5 1H12V0h-1v1H5V0H4zm0 2v1h1V2h6v1h1V2h2.5a.5.5 0 01.5.5V4H1V2.5a.5.5 0 01.5-.5H4zM1 5h14v9.5a.5.5 0 01-.5.5h-13a.5.5 0 01-.5-.5V5z" />
        </svg>
        {fmtDisplay(from)} — {fmtDisplay(to)}
      </button>
      {open && (
        <>
          <div className="dc-cal-backdrop" onClick={() => setOpen(false)} />
          <div className="dc-cal-drop">
            <div className="dc-cal-header">
              <button className="dc-cal-nav" onClick={prevMonth}>
                &#9664;
              </button>
              <span className="dc-cal-title">
                {MONTH_NAMES[viewMonth]} {viewYear}
              </span>
              <button className="dc-cal-nav" onClick={nextMonth}>
                &#9654;
              </button>
            </div>
            <div className="dc-cal-grid">
              {DOW.map((d) => (
                <div key={d} className="dc-cal-dow">
                  {d}
                </div>
              ))}
              {cells.map((cell, i) => (
                <div
                  key={i}
                  className={dayClass(cell)}
                  onClick={() => handleDayClick(cell)}
                >
                  {cell.day}
                </div>
              ))}
            </div>
            {picking && (
              <div
                style={{
                  textAlign: 'center',
                  marginTop: 8,
                  fontSize: 10,
                  color: '#26c6da',
                }}
              >
                Click end date
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function fmt(n: number): string {
  if (n >= 1e9) return (n / 1e9).toFixed(1) + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return n.toString();
}
function fmtBytes(b: number): string {
  if (b >= 1e9) return (b / 1e9).toFixed(2) + ' GB';
  if (b >= 1e6) return (b / 1e6).toFixed(1) + ' MB';
  return (b / 1e3).toFixed(0) + ' KB';
}
function fmtDate(s: string): string {
  return s ? s.slice(0, 10) : '—';
}
function heatClass(count: number, expected: number): string {
  if (count === 0) return 'dc-hm-empty';
  const r = count / expected;
  if (r >= 0.9) return 'dc-hm-full';
  if (r >= 0.6) return 'dc-hm-high';
  if (r >= 0.3) return 'dc-hm-mid';
  return 'dc-hm-low';
}

// Old canvas chart utilities removed — now using TradingView Lightweight Charts (ExplorerCharts.tsx)

// ── Heatmap date helpers ──────────────────────────────────────────────────────
function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}
function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function diffDays(a: string, b: string): number {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86400000);
}

const DB_OPTIONS = [
  { value: 'binance_raw', label: 'Raw' },
  { value: 'binance_cleaned', label: 'Cleaned' },
  { value: 'binance_spike', label: 'Spike' },
  { value: 'binance_preprocessed', label: 'Preprocessed' },
];

const INTERVAL_OPTIONS = ['1m', '5m', '15m', '30m', '1h', '4h', '1d'] as const;

// Tables differ per database
function getOhlcvTable(db: string, market: string, interval: string): string {
  if (db === 'binance_raw') {
    // Raw only has _1m and _5m tables
    const suffix = interval === '1m' ? '1m' : '5m';
    return market === 'spot'
      ? `spot_ohlcv_${suffix}`
      : `futures_ohlcv_${suffix}`;
  }
  return market === 'spot' ? 'spot_ohlcv' : 'futures_ohlcv';
}

// SQL grouping function and expected bars per slot
function intervalToSql(interval: string): {
  groupFn: string;
  expected: number;
  labelMod: number;
} {
  switch (interval) {
    case '1m':
      return {
        groupFn: 'toStartOfMinute(open_time)',
        expected: 1,
        labelMod: 60,
      };
    case '5m':
      return {
        groupFn: 'toStartOfFiveMinutes(open_time)',
        expected: 1,
        labelMod: 12,
      };
    case '15m':
      return {
        groupFn: 'toStartOfFifteenMinutes(open_time)',
        expected: 3,
        labelMod: 4,
      };
    case '30m':
      return {
        groupFn: 'toStartOfInterval(open_time, INTERVAL 30 MINUTE)',
        expected: 6,
        labelMod: 2,
      };
    case '1h':
      return { groupFn: 'toStartOfHour(open_time)', expected: 12, labelMod: 4 };
    case '4h':
      return {
        groupFn: 'toStartOfInterval(open_time, INTERVAL 4 HOUR)',
        expected: 48,
        labelMod: 2,
      };
    case '1d':
      return { groupFn: 'toStartOfDay(open_time)', expected: 288, labelMod: 1 };
    default:
      return { groupFn: 'toStartOfHour(open_time)', expected: 12, labelMod: 4 };
  }
}

// For raw db, interval column doesn't exist — we can only query 1m or 5m
// For non-raw, we filter by the interval column
function getIntervalFilter(db: string, interval: string): string {
  if (db === 'binance_raw') return '';
  return ` AND interval = '${interval}'`;
}
function dbHasOhlcv(db: string): boolean {
  return db !== 'binance_preprocessed';
}
function dbHasFeatures(db: string): boolean {
  return (
    db === 'binance_raw' ||
    db === 'binance_preprocessed' ||
    db === 'binance_cleaned' ||
    db === 'binance_spike'
  );
}

// ── Heatmap data fetcher (standalone, usable outside React) ───────────────────
async function fetchHeatmapData(
  market: string,
  db: string,
  dateFrom: string,
  dateTo: string,
  interval: string,
): Promise<{ coins: HeatmapCoin[]; maxTime: string }> {
  const hours = diffDays(dateFrom, dateTo) * 24;
  if (hours <= 0) return { coins: [], maxTime: '' };

  const hasOhlcv = dbHasOhlcv(db);
  const ohlcvTable = getOhlcvTable(db, market, interval);
  const { groupFn } = intervalToSql(interval);
  const intervalFilter = getIntervalFilter(db, interval);

  let anchorRaw = '';
  if (hasOhlcv) {
    const maxRows = await chQuery<{ mt: string }>(
      `SELECT max(open_time) AS mt FROM ${db}.${ohlcvTable}`,
    );
    anchorRaw = maxRows[0]?.mt ?? '';
  } else {
    const maxRows = await chQuery<{ mt: string }>(
      `SELECT max(open_time) AS mt FROM ${db}.open_interest`,
    );
    anchorRaw = maxRows[0]?.mt ?? '';
  }

  const fromTs = `${dateFrom} 00:00:00`;
  const toTs = `${dateTo} 23:59:59`;

  let ohlcvRows: { symbol: string; slot: string; cnt: string }[] = [];
  if (hasOhlcv) {
    ohlcvRows = await chQuery<{ symbol: string; slot: string; cnt: string }>(`
      SELECT symbol, ${groupFn} AS slot, count() AS cnt
      FROM ${db}.${ohlcvTable} FINAL
      WHERE open_time >= toDateTime('${fromTs}') AND open_time <= toDateTime('${toTs}')${intervalFilter}
      GROUP BY symbol, slot ORDER BY symbol, slot
    `);
  }

  const featureTables = [
    'open_interest',
    'global_ls_ratio',
    'top_ls_account',
    'top_ls_position',
    'taker_buy_sell',
    'spot_depth',
    'futures_depth',
  ];
  const featGroupFn = groupFn.replace('open_time', 'open_time');
  const featDb = db;
  const featureRows = dbHasFeatures(db)
    ? await Promise.all(
        featureTables.map((ft) =>
          chQuery<{ symbol: string; slot: string }>(`
      SELECT symbol, ${featGroupFn} AS slot
      FROM ${featDb}.${ft} FINAL
      WHERE open_time >= toDateTime('${fromTs}') AND open_time <= toDateTime('${toTs}')
      GROUP BY symbol, slot
    `).then((rows) => rows.map((r) => ({ ...r, ft }))),
        ),
      )
    : [];

  const frGroupFn = groupFn.replace(/open_time/g, 'funding_time');
  const frRows = dbHasFeatures(db)
    ? await chQuery<{ symbol: string; slot: string }>(`
    SELECT symbol, ${frGroupFn} AS slot
    FROM ${featDb}.funding_rate FINAL
    WHERE funding_time >= toDateTime('${fromTs}') AND funding_time <= toDateTime('${toTs}')
    GROUP BY symbol, slot
  `).then((rows) => rows.map((r) => ({ ...r, ft: 'funding_rate' })))
    : [];

  const featureMap: Record<string, Set<string>> = {};
  for (const row of [...featureRows.flat(), ...frRows]) {
    const key = `${row.symbol}|${row.slot}`;
    if (!featureMap[key]) featureMap[key] = new Set();
    featureMap[key].add(row.ft);
  }

  const coinMap: Record<
    string,
    Record<string, { cnt: number; features: string[] }>
  > = {};
  for (const row of ohlcvRows) {
    if (!coinMap[row.symbol]) coinMap[row.symbol] = {};
    const key = `${row.symbol}|${row.slot}`;
    coinMap[row.symbol][row.slot] = {
      cnt: parseInt(row.cnt),
      features: Array.from(featureMap[key] ?? []),
    };
  }

  if (!hasOhlcv) {
    for (const [key, feats] of Object.entries(featureMap)) {
      const [sym, hour] = key.split('|');
      if (!coinMap[sym]) coinMap[sym] = {};
      if (!coinMap[sym][hour]) coinMap[sym][hour] = { cnt: 0, features: [] };
      coinMap[sym][hour].features = Array.from(feats);
    }
  }

  const coins = Object.entries(coinMap).map(([symbol, hours]) => ({
    symbol,
    slots: Object.entries(hours)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([hour, v]) => ({
        hour,
        ohlcv_count: v.cnt,
        features: v.features,
      })),
  }));
  return { coins, maxTime: anchorRaw };
}

// ── Heatmap cache (survives unmount/remount, prefetched on app load) ──────────
const heatmapCache: {
  key: string;
  coins: HeatmapCoin[];
  maxTime: string;
  promise: Promise<void> | null;
} = { key: '', coins: [], maxTime: '', promise: null };

// Prefetch with default filters on module load
const DEFAULT_HM = {
  market: 'spot',
  db: 'binance_raw',
  dateFrom: daysAgo(3),
  dateTo: todayStr(),
  interval: '1h',
};
heatmapCache.promise = fetchHeatmapData(
  DEFAULT_HM.market,
  DEFAULT_HM.db,
  DEFAULT_HM.dateFrom,
  DEFAULT_HM.dateTo,
  DEFAULT_HM.interval,
)
  .then(({ coins, maxTime }) => {
    const key = `${DEFAULT_HM.market}|${DEFAULT_HM.db}|${DEFAULT_HM.dateFrom}|${DEFAULT_HM.dateTo}|${DEFAULT_HM.interval}`;
    heatmapCache.key = key;
    heatmapCache.coins = coins;
    heatmapCache.maxTime = maxTime;
  })
  .catch(() => {});

// ── Heatmap Panel ──────────────────────────────────────────────────────────────
function HeatmapPanel({
  summary,
  market,
  db,
  dateFrom,
  dateTo,
  interval,
}: {
  summary: SummaryInfo | null;
  market: 'spot' | 'futures';
  db: string;
  dateFrom: string;
  dateTo: string;
  interval: string;
}) {
  const { disabledCoins: heatmapDisabled } = useCoinRegistry();
  const cacheKey = `${market}|${db}|${dateFrom}|${dateTo}|${interval}`;
  const cached = heatmapCache.key === cacheKey;
  const [coins, setCoins] = useState<HeatmapCoin[]>(
    cached ? heatmapCache.coins : [],
  );
  const [maxTime, setMaxTime] = useState(cached ? heatmapCache.maxTime : '');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Wait for any in-flight prefetch before deciding if cache is fresh
      if (heatmapCache.promise) await heatmapCache.promise;
      if (heatmapCache.key === cacheKey) {
        setCoins(heatmapCache.coins);
        setMaxTime(heatmapCache.maxTime);
      }
      const { coins: newCoins, maxTime: newMaxTime } = await fetchHeatmapData(
        market,
        db,
        dateFrom,
        dateTo,
        interval,
      );
      setCoins(newCoins);
      setMaxTime(newMaxTime);
      heatmapCache.key = cacheKey;
      heatmapCache.coins = newCoins;
      heatmapCache.maxTime = newMaxTime;
    } finally {
      setLoading(false);
    }
  }, [market, db, dateFrom, dateTo, interval, cacheKey]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh every 60s
  useEffect(() => {
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const { expected: expCount, labelMod } = intervalToSql(interval);

  const hourLabels: string[] = [];
  const seen = new Set<string>();
  for (const coin of coins)
    for (const s of coin.slots)
      if (!seen.has(s.hour)) {
        seen.add(s.hour);
        hourLabels.push(s.hour);
      }
  hourLabels.sort();

  const slotMap: Record<string, Record<string, HeatmapSlot>> = {};
  for (const coin of coins) {
    slotMap[coin.symbol] = {};
    for (const s of coin.slots) slotMap[coin.symbol][s.hour] = s;
  }

  function coverage(sym: string): number {
    const m = slotMap[sym];
    if (!m) return 0;
    const filled = Object.values(m).filter((s) => s.ohlcv_count > 0).length;
    return hourLabels.length > 0
      ? Math.round((filled / hourLabels.length) * 100)
      : 0;
  }

  const totalRows = summary ? summary.spotRows + summary.futuresRows : 0;
  const diskTotal = summary
    ? Object.values(summary.disk).reduce((a, b) => a + b, 0)
    : 0;
  const lastUpdate = maxTime ? maxTime.slice(0, 16).replace('T', ' ') : '—';

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      <div className="dc-sidebar">
        <div className="dc-sidebar-section">
          <div className="dc-sidebar-label">Source</div>
          <div className="dc-sidebar-sub">
            <span>Binance</span>
          </div>
          <div className="dc-sidebar-sub">
            <span>{interval}</span>
          </div>
        </div>
        <div className="dc-sidebar-section">
          <div className="dc-sidebar-label">Total rows</div>
          <div className="dc-sidebar-value">{fmt(totalRows)}</div>
        </div>
        {summary && (
          <div className="dc-sidebar-section">
            <div className="dc-sidebar-label">Coverage</div>
            <div className="dc-sidebar-sub">
              SWAP <span>{fmt(summary.futuresRows)}</span>
            </div>
            <div className="dc-sidebar-sub">
              SPOT <span>{fmt(summary.spotRows)}</span>
            </div>
          </div>
        )}
        <div className="dc-sidebar-section">
          <div className="dc-sidebar-label">Features</div>
          <div className="dc-legend-item">
            <span
              className="dc-legend-dot"
              style={{ background: '#00e676' }}
            ></span>
            Full
          </div>
          <div className="dc-legend-item">
            <span
              className="dc-legend-dot"
              style={{ background: '#00e676', opacity: 0.6 }}
            ></span>
            OHLCV+OI
          </div>
          <div className="dc-legend-item">
            <span
              className="dc-legend-dot"
              style={{ background: '#ffa726' }}
            ></span>
            OHLCV
          </div>
          <div className="dc-legend-item">
            <span
              className="dc-legend-dot"
              style={{ background: '#243040' }}
            ></span>
            Missing
          </div>
        </div>
        {summary && (
          <div className="dc-sidebar-section">
            <div className="dc-sidebar-label">Storage</div>
            <div className="dc-sidebar-sub">
              Disk <span>{fmtBytes(diskTotal)}</span>
            </div>
            <div className="dc-sidebar-sub">
              From <span>{fmtDate(summary.minTime)}</span>
            </div>
            <div className="dc-sidebar-sub">
              To <span>{fmtDate(summary.maxTime)}</span>
            </div>
          </div>
        )}
        <div className="dc-sidebar-section">
          <div className="dc-sidebar-label">Last update</div>
          <div className="dc-sidebar-sub">
            <span>{lastUpdate}</span>
          </div>
        </div>
      </div>

      <div className="dc-heatmap-container">
        <table className="dc-heatmap-table">
          <thead>
            <tr>
              <th
                className="dc-sticky-col dc-col-status"
                style={{ left: 0, minWidth: 16 }}
              ></th>
              <th
                className="dc-sticky-col dc-col-coin"
                style={{ left: 16, minWidth: 65, textAlign: 'left' }}
              >
                COIN
              </th>
              {hourLabels.map((h, i) => (
                <th key={h} style={{ minWidth: 22 }}>
                  {i % labelMod === 0 ? h.slice(5, 13) : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(() => {
              const allDbCoins = (
                summary
                  ? summary.coins.map((c) => c.symbol)
                  : Object.keys(slotMap)
              ).filter((s) => !heatmapDisabled.has(s.replace(/USDT$/, '')));
              const categorized = new Set(Object.values(CATEGORIES).flat());
              const allCategories: [string, string[]][] = [
                ...Object.entries(CATEGORIES),
                ['OTHER', allDbCoins.filter((s) => !categorized.has(s)).sort()],
              ];
              return allCategories.map(([cat, catCoins]) => {
                const existing = catCoins.filter(
                  (c) => allDbCoins.includes(c) || slotMap[c],
                );
                if (!existing.length) return null;
                return [
                  <tr key={`cat-${cat}`} className="dc-category-row">
                    <td colSpan={2 + hourLabels.length}>{cat}</td>
                  </tr>,
                  ...existing.map((sym) => {
                    const cov = coverage(sym);
                    const statusClass =
                      cov >= 95
                        ? 'dc-online'
                        : cov >= 50
                          ? 'dc-partial'
                          : 'dc-offline';
                    return (
                      <tr key={sym} className="dc-coin-row">
                        <td className="dc-sticky-col dc-col-status">
                          <span
                            className={`dc-status-dot ${statusClass}`}
                          ></span>
                        </td>
                        <td className="dc-sticky-col dc-col-coin">
                          <span className="dc-coin-name">
                            {sym.replace('USDT', '')}
                          </span>
                        </td>
                        {hourLabels.map((h) => {
                          const slot = slotMap[sym]?.[h];
                          const cnt = slot?.ohlcv_count ?? 0;
                          const hasAll =
                            (slot?.features.length ?? 0) ===
                            ALL_FEATURES.length;
                          const hasOI =
                            slot?.features.includes('open_interest');
                          const cls =
                            cnt === 0
                              ? 'dc-hm-empty'
                              : hasAll
                                ? 'dc-hm-full'
                                : hasOI
                                  ? 'dc-hm-high'
                                  : heatClass(cnt, expCount);
                          const featNames: Record<string, string> = {
                            open_interest: 'Open Interest',
                            global_ls_ratio: 'Global L/S Ratio',
                            top_ls_account: 'Top L/S Account',
                            top_ls_position: 'Top L/S Position',
                            taker_buy_sell: 'Taker Buy/Sell',
                            funding_rate: 'Funding Rate',
                            spot_depth: 'Spot Depth',
                            futures_depth: 'Futures Depth',
                          };
                          const title = slot
                            ? `${h}\n${cnt} bars\nFeatures: ${slot.features.map((f) => featNames[f] ?? f).join(', ') || 'none'}`
                            : `${h}\nNo data`;
                          return (
                            <td key={h} className="dc-hm-cell">
                              <div
                                className={`dc-hm-block ${cls}`}
                                title={title}
                              ></div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  }),
                ];
              });
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Mini SVG diagrams for pipeline steps ──────────────────────────────────────

// Candlestick bars with a gap, then the gap filled in
function GapFillDiagram() {
  const W = 380,
    H = 120;
  const bars = [
    { o: 60, c: 42, h: 35, l: 68 },
    { o: 45, c: 55, h: 38, l: 72 },
    null,
    null, // gap
    { o: 50, c: 40, h: 30, l: 60 },
    { o: 42, c: 52, h: 28, l: 62 },
  ];
  const bw = 20,
    gap = 8,
    startX = 12;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H }}>
      <text
        x={startX}
        y={16}
        fill="#5a6a7a"
        fontSize={12}
        fontFamily="monospace"
      >
        BEFORE
      </text>
      {bars.map((b, i) => {
        const x = startX + i * (bw + gap);
        if (!b)
          return (
            <g key={i}>
              <rect
                x={x}
                y={24}
                width={bw}
                height={80}
                rx={3}
                fill="rgba(239,83,80,0.08)"
                stroke="rgba(239,83,80,0.3)"
                strokeWidth={1}
                strokeDasharray="4 3"
              />
              <text
                x={x + bw / 2}
                y={68}
                textAnchor="middle"
                fill="#ef5350"
                fontSize={14}
              >
                ?
              </text>
            </g>
          );
        const green = b.c > b.o;
        const bodyTop = Math.min(b.o, b.c),
          bodyBot = Math.max(b.o, b.c);
        const color = green ? '#00e676' : '#ef5350';
        return (
          <g key={i}>
            <line
              x1={x + bw / 2}
              y1={b.h + 22}
              x2={x + bw / 2}
              y2={b.l + 22}
              stroke={color}
              strokeWidth={1.5}
            />
            <rect
              x={x + 3}
              y={bodyTop + 22}
              width={bw - 6}
              height={Math.max(bodyBot - bodyTop, 3)}
              fill={color}
              rx={1}
            />
          </g>
        );
      })}
      {/* Arrow */}
      <line
        x1={180}
        y1={64}
        x2={202}
        y2={64}
        stroke="#2a3a50"
        strokeWidth={2}
      />
      <polygon points="202,59 212,64 202,69" fill="#2a3a50" />
      {/* After */}
      <text x={220} y={16} fill="#5a6a7a" fontSize={12} fontFamily="monospace">
        AFTER
      </text>
      {bars.map((b, i) => {
        const x = 220 + i * (bw + gap);
        const filled = !b;
        const bar = b ?? { o: 48, c: 48, h: 48, l: 48 };
        const green = bar.c > bar.o;
        const bodyTop = Math.min(bar.o, bar.c),
          bodyBot = Math.max(bar.o, bar.c);
        const color = filled ? '#26c6da' : green ? '#00e676' : '#ef5350';
        return (
          <g key={`a${i}`}>
            <line
              x1={x + bw / 2}
              y1={bar.h + 22}
              x2={x + bw / 2}
              y2={bar.l + 22}
              stroke={color}
              strokeWidth={1.5}
            />
            <rect
              x={x + 3}
              y={bodyTop + 22}
              width={bw - 6}
              height={Math.max(bodyBot - bodyTop, 3)}
              fill={color}
              rx={1}
              opacity={filled ? 0.7 : 1}
            />
            {filled && (
              <text
                x={x + bw / 2}
                y={bodyTop + 16}
                textAnchor="middle"
                fill="#26c6da"
                fontSize={10}
              >
                OKX
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// Bar with a huge spike wick, then clamped
function SpikeCleanDiagram() {
  const W = 380,
    H = 120;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H }}>
      <text x={12} y={16} fill="#5a6a7a" fontSize={12} fontFamily="monospace">
        DETECT
      </text>
      {/* Normal bars */}
      {[0, 1, 3, 4].map((i) => {
        const x = 12 + i * 28;
        return (
          <g key={i}>
            <line
              x1={x + 10}
              y1={50}
              x2={x + 10}
              y2={82}
              stroke="#00e676"
              strokeWidth={1.5}
            />
            <rect
              x={x + 4}
              y={56}
              width={12}
              height={16}
              fill="#00e676"
              rx={1}
            />
          </g>
        );
      })}
      {/* Spike bar */}
      <line x1={82} y1={10} x2={82} y2={105} stroke="#ef5350" strokeWidth={2} />
      <rect x={76} y={54} width={12} height={20} fill="#ef5350" rx={1} />
      {/* Spike annotation */}
      <line
        x1={94}
        y1={10}
        x2={108}
        y2={10}
        stroke="rgba(239,83,80,0.5)"
        strokeWidth={0.5}
        strokeDasharray="3 2"
      />
      <text x={112} y={14} fill="#ef5350" fontSize={11} fontFamily="monospace">
        wick &gt;5%
      </text>
      <line
        x1={94}
        y1={105}
        x2={108}
        y2={105}
        stroke="rgba(239,83,80,0.5)"
        strokeWidth={0.5}
        strokeDasharray="3 2"
      />
      <text x={112} y={109} fill="#ef5350" fontSize={11} fontFamily="monospace">
        flash wick
      </text>
      {/* Arrow */}
      <line
        x1={180}
        y1={64}
        x2={202}
        y2={64}
        stroke="#2a3a50"
        strokeWidth={2}
      />
      <polygon points="202,59 212,64 202,69" fill="#2a3a50" />
      {/* After: clamped */}
      <text x={220} y={16} fill="#5a6a7a" fontSize={12} fontFamily="monospace">
        CLAMP
      </text>
      {[0, 1, 2, 3, 4].map((i) => {
        const x = 220 + i * 28;
        const isClamped = i === 2;
        return (
          <g key={i}>
            <line
              x1={x + 10}
              y1={isClamped ? 52 : 50}
              x2={x + 10}
              y2={isClamped ? 78 : 82}
              stroke={isClamped ? '#ffa726' : '#00e676'}
              strokeWidth={1.5}
            />
            <rect
              x={x + 4}
              y={isClamped ? 54 : 56}
              width={12}
              height={isClamped ? 20 : 16}
              fill={isClamped ? '#ffa726' : '#00e676'}
              rx={1}
            />
            {isClamped && (
              <>
                <line
                  x1={x}
                  y1={52}
                  x2={x + 20}
                  y2={52}
                  stroke="#ffa726"
                  strokeWidth={0.5}
                  strokeDasharray="3 2"
                />
                <line
                  x1={x}
                  y1={78}
                  x2={x + 20}
                  y2={78}
                  stroke="#ffa726"
                  strokeWidth={0.5}
                  strokeDasharray="3 2"
                />
              </>
            )}
          </g>
        );
      })}
      <text x={282} y={48} fill="#ffa726" fontSize={11} fontFamily="monospace">
        body+0.1%
      </text>
    </svg>
  );
}

// 5m bars being grouped into larger timeframes
function ResampleDiagram() {
  const W = 380,
    H = 110;
  const intervals = ['5m', '15m', '30m', '1h', '4h', '1d'];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H }}>
      <text x={12} y={16} fill="#5a6a7a" fontSize={12} fontFamily="monospace">
        5m BASE
      </text>
      {/* 12 tiny 5m bars */}
      {Array.from({ length: 12 }).map((_, i) => {
        const x = 12 + i * 13;
        const h = 18 + Math.sin(i * 0.8) * 12;
        return (
          <rect
            key={i}
            x={x}
            y={80 - h}
            width={10}
            height={h}
            fill="rgba(66,165,245,0.5)"
            rx={1}
          />
        );
      })}
      {/* Arrow */}
      <line
        x1={180}
        y1={56}
        x2={202}
        y2={56}
        stroke="#2a3a50"
        strokeWidth={2}
      />
      <polygon points="202,51 212,56 202,61" fill="#2a3a50" />
      {/* Timeframe blocks */}
      <text x={220} y={16} fill="#5a6a7a" fontSize={12} fontFamily="monospace">
        RESAMPLE
      </text>
      {intervals.map((iv, i) => {
        const x = 220 + (i % 3) * 52;
        const y = 24 + Math.floor(i / 3) * 38;
        const colors = [
          '#42a5f5',
          '#26c6da',
          '#00e676',
          '#ffa726',
          '#ab47bc',
          '#ef5350',
        ];
        return (
          <g key={iv}>
            <rect
              x={x}
              y={y}
              width={46}
              height={30}
              rx={4}
              fill={`${colors[i]}15`}
              stroke={`${colors[i]}44`}
              strokeWidth={1}
            />
            <text
              x={x + 23}
              y={y + 19}
              textAnchor="middle"
              fill={colors[i]}
              fontSize={13}
              fontFamily="monospace"
              fontWeight={700}
            >
              {iv}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// Feature normalization: camelCase → snake_case + aggregation
function FeatureNormDiagram() {
  const W = 380,
    H = 110;
  const raw = ['sumOpenInterest', 'longShortRatio', 'buyVol'];
  const clean = ['sum_open_interest', 'long_short_ratio', 'buy_vol'];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H }}>
      <text x={12} y={16} fill="#5a6a7a" fontSize={12} fontFamily="monospace">
        RAW (camelCase)
      </text>
      {raw.map((r, i) => (
        <g key={r}>
          <rect
            x={12}
            y={24 + i * 27}
            width={130}
            height={22}
            rx={3}
            fill="rgba(66,165,245,0.08)"
            stroke="rgba(66,165,245,0.25)"
            strokeWidth={0.5}
          />
          <text
            x={77}
            y={39 + i * 27}
            textAnchor="middle"
            fill="#42a5f5"
            fontSize={11}
            fontFamily="monospace"
          >
            {r}
          </text>
        </g>
      ))}
      {/* Arrow */}
      <line
        x1={152}
        y1={56}
        x2={178}
        y2={56}
        stroke="#2a3a50"
        strokeWidth={2}
      />
      <polygon points="178,51 188,56 178,61" fill="#2a3a50" />
      <text x={198} y={16} fill="#5a6a7a" fontSize={12} fontFamily="monospace">
        CLEAN (snake_case)
      </text>
      {clean.map((c, i) => (
        <g key={c}>
          <rect
            x={198}
            y={24 + i * 27}
            width={168}
            height={22}
            rx={3}
            fill="rgba(0,230,118,0.08)"
            stroke="rgba(0,230,118,0.25)"
            strokeWidth={0.5}
          />
          <text
            x={282}
            y={39 + i * 27}
            textAnchor="middle"
            fill="#00e676"
            fontSize={11}
            fontFamily="monospace"
          >
            {c}
          </text>
        </g>
      ))}
    </svg>
  );
}

// Validation checks
function ValidationDiagram() {
  const W = 380,
    H = 110;
  const checks = [
    { label: 'No duplicates', ok: true },
    { label: 'Monotonic timestamps', ok: true },
    { label: 'high >= low', ok: true },
    { label: 'No NaN OHLCV', ok: true },
    { label: 'Coverage > 99%', ok: true },
    { label: 'Spot-Fut corr > 0.95', ok: true },
  ];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H }}>
      {checks.map((ch, i) => {
        const col = i % 2,
          row = Math.floor(i / 2);
        const x = 12 + col * 190,
          y = 6 + row * 34;
        return (
          <g key={ch.label}>
            <rect
              x={x}
              y={y}
              width={178}
              height={28}
              rx={4}
              fill="rgba(0,230,118,0.06)"
              stroke="rgba(0,230,118,0.2)"
              strokeWidth={0.5}
            />
            <circle cx={x + 16} cy={y + 14} r={6} fill="#00e676" />
            <text
              x={x + 15.5}
              y={y + 18}
              textAnchor="middle"
              fill="#0a0e14"
              fontSize={10}
              fontWeight={700}
            >
              &#10003;
            </text>
            <text
              x={x + 30}
              y={y + 18}
              fill="#c5cdd8"
              fontSize={11}
              fontFamily="monospace"
            >
              {ch.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ── Report Panel ───────────────────────────────────────────────────────────────
function ReportPanel({ summary }: { summary: SummaryInfo | null }) {
  if (!summary)
    return (
      <div className="dc-loading">
        <div className="dc-spinner" />
        &nbsp;Loading...
      </div>
    );

  const diskTotal = Object.values(summary.disk).reduce((a, b) => a + b, 0);
  const maxDisk = Math.max(...Object.values(summary.disk), 1);
  const dbColors: Record<string, string> = {
    binance_raw: '#42a5f5',
    binance_cleaned: '#00e676',
    binance_spike: '#ffa726',
    binance_preprocessed: '#ab47bc',
  };

  // Pipeline step card
  const StepCard = ({
    num,
    color,
    title,
    desc,
    children,
  }: {
    num: string;
    color: string;
    title: string;
    desc: string;
    children: React.ReactNode;
  }) => (
    <div
      style={{
        background: '#111820',
        border: `1px solid ${color}33`,
        borderRadius: 12,
        padding: 0,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '18px 24px 0' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            marginBottom: 10,
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              background: `${color}22`,
              border: `2px solid ${color}66`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 15,
              fontWeight: 800,
              color,
              flexShrink: 0,
            }}
          >
            {num}
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#e8edf3' }}>
              {title}
            </div>
            <div style={{ fontSize: 13, color: '#5a6a7a', lineHeight: 1.4 }}>
              {desc}
            </div>
          </div>
        </div>
      </div>
      <div style={{ padding: '10px 20px 18px' }}>{children}</div>
    </div>
  );

  // Vertical connector between steps
  const Connector = ({ label: lbl }: { label?: string }) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0',
        height: 40,
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: 0,
          bottom: 0,
          width: 2,
          background: '#2a3a50',
        }}
      />
      {lbl && (
        <div
          style={{
            position: 'relative',
            fontSize: 12,
            color: '#5a6a7a',
            background: '#0a0e14',
            padding: '3px 14px',
            border: '1px solid #1a2332',
            borderRadius: 12,
            zIndex: 1,
          }}
        >
          {lbl}
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          left: 'calc(50% - 5px)',
          bottom: 0,
          width: 0,
          height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: '8px solid #2a3a50',
        }}
      />
    </div>
  );

  return (
    <div className="dc-pane" style={{ maxWidth: 960, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: '#e8edf3',
            letterSpacing: '-0.5px',
          }}
        >
          Data Preprocessing Pipeline
        </div>
        <div style={{ fontSize: 14, color: '#5a6a7a', marginTop: 6 }}>
          {summary.coins.length} coins · {fmtDate(summary.minTime)} to{' '}
          {fmtDate(summary.maxTime)} · {fmtBytes(diskTotal)} on disk
        </div>
      </div>

      {/* ── Step 0: Source ── */}
      <StepCard
        num="0"
        color="#26c6da"
        title="Data Collection"
        desc="Binance REST API  ·  Spot + Futures OHLCV (5m/1m)  ·  OI, L/S Ratios, Taker, Funding Rate"
      >
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            'Spot OHLCV',
            'Futures OHLCV',
            'Open Interest',
            'Global L/S',
            'Top L/S Acct',
            'Top L/S Pos',
            'Taker B/S',
            'Funding Rate',
          ].map((d) => (
            <div
              key={d}
              style={{
                background: 'rgba(38,198,218,0.08)',
                border: '1px solid rgba(38,198,218,0.2)',
                borderRadius: 5,
                padding: '5px 14px',
                fontSize: 13,
                color: '#26c6da',
                fontWeight: 600,
              }}
            >
              {d}
            </div>
          ))}
        </div>
      </StepCard>

      <Connector label="ingest to ClickHouse" />

      {/* ── Step 1: Raw Storage ── */}
      <StepCard
        num="1"
        color="#42a5f5"
        title="Raw Storage"
        desc="binance_raw  ·  Unmodified source of truth  ·  Never mutated"
      >
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { l: 'spot_ohlcv_5m', v: fmt(summary.spotRows) },
            { l: 'futures_ohlcv_5m', v: fmt(summary.futuresRows) },
            { l: 'spot_ohlcv_1m', v: fmt(summary.spot1mRows) },
            { l: 'futures_ohlcv_1m', v: fmt(summary.futures1mRows) },
          ].map((t) => (
            <div
              key={t.l}
              style={{
                background: 'rgba(66,165,245,0.08)',
                borderRadius: 5,
                padding: '6px 14px',
              }}
            >
              <div style={{ fontSize: 11, color: '#5a6a7a' }}>{t.l}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#42a5f5' }}>
                {t.v}
              </div>
            </div>
          ))}
        </div>
      </StepCard>

      <Connector label="clean_binance_ohlcv.py" />

      {/* ── Step 2: Gap Fill ── */}
      <StepCard
        num="2"
        color="#26c6da"
        title="Gap Filling"
        desc="Fill missing 5m bars  ·  OKX cross-exchange first, then forward-fill  ·  142 gaps across 23 coins"
      >
        <GapFillDiagram />
        <div style={{ display: 'flex', gap: 20, marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: '#26c6da',
              }}
            />
            <span style={{ fontSize: 13, color: '#5a6a7a' }}>
              OKX fill (real prices)
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: '#5a6a7a',
              }}
            />
            <span style={{ fontSize: 13, color: '#5a6a7a' }}>
              Forward-fill (close=prev, vol=0)
            </span>
          </div>
        </div>
      </StepCard>

      <Connector label="detect_anomalous_spikes.py  ·  clean_anomalous_spikes.py" />

      {/* ── Step 3: Spike Cleaning ── */}
      <StepCard
        num="3"
        color="#ffa726"
        title="Spike Detection & Cleaning"
        desc="Detect flash wicks (>5% wick, >10x range)  ·  Clamp to body ± 0.1%  ·  Legitimate moves preserved"
      >
        <SpikeCleanDiagram />
        <div style={{ display: 'flex', gap: 20, marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: '#ef5350',
              }}
            />
            <span style={{ fontSize: 13, color: '#5a6a7a' }}>
              Flash wick detected
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: '#ffa726',
              }}
            />
            <span style={{ fontSize: 13, color: '#5a6a7a' }}>
              Clamped (body preserved)
            </span>
          </div>
        </div>
      </StepCard>

      <Connector />

      {/* Fork: spike vs cleaned */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div
          style={{
            background: '#111820',
            border: '1px solid rgba(255,167,38,0.25)',
            borderRadius: 12,
            padding: '18px 24px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 700, color: '#ffa726' }}>
            binance_spike
          </div>
          <div style={{ fontSize: 13, color: '#5a6a7a', marginTop: 4 }}>
            Gap-filled, spikes kept
          </div>
          <div style={{ fontSize: 12, color: '#5a6a7a', marginTop: 6 }}>
            For research & comparison
          </div>
        </div>
        <div
          style={{
            background: '#111820',
            border: '1px solid rgba(0,230,118,0.25)',
            borderRadius: 12,
            padding: '18px 24px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 700, color: '#00e676' }}>
            binance_cleaned
          </div>
          <div style={{ fontSize: 13, color: '#5a6a7a', marginTop: 4 }}>
            Gap-filled, spikes clamped
          </div>
          <div style={{ fontSize: 12, color: '#5a6a7a', marginTop: 6 }}>
            Primary clean dataset
          </div>
        </div>
      </div>

      <Connector label="resample_binance_ohlcv.py  ·  resample_binance_features.py" />

      {/* ── Step 4: Resampling ── */}
      <StepCard
        num="4"
        color="#ab47bc"
        title="Resampling & Feature Normalization"
        desc="5m base → 6 timeframes  ·  camelCase → snake_case  ·  Feature-specific aggregation (last/sum)"
      >
        <div
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                color: '#5a6a7a',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              OHLCV Resampling
            </div>
            <ResampleDiagram />
          </div>
          <div>
            <div
              style={{
                fontSize: 12,
                color: '#5a6a7a',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Feature Normalization
            </div>
            <FeatureNormDiagram />
          </div>
        </div>
        <div
          style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}
        >
          {[
            {
              feat: 'OI / L-S Ratios',
              agg: 'last',
              why: 'point-in-time snapshot',
            },
            { feat: 'Taker B/S Vol', agg: 'sum', why: 'cumulative flow' },
            { feat: 'Funding Rate', agg: 'sum', why: 'additive charge' },
          ].map((r) => (
            <div
              key={r.feat}
              style={{
                background: 'rgba(171,71,188,0.06)',
                border: '1px solid rgba(171,71,188,0.15)',
                borderRadius: 5,
                padding: '8px 14px',
                flex: '1 1 180px',
              }}
            >
              <div style={{ fontSize: 13, color: '#ab47bc', fontWeight: 600 }}>
                {r.feat}
              </div>
              <div style={{ fontSize: 12, color: '#5a6a7a' }}>
                agg: <span style={{ color: '#c5cdd8' }}>{r.agg}</span> — {r.why}
              </div>
            </div>
          ))}
        </div>
      </StepCard>

      <Connector label="binance_preprocessed" />

      {/* ── Step 5: Validation ── */}
      <StepCard
        num="5"
        color="#00e676"
        title="Validation"
        desc="Structural integrity  ·  OHLCV constraints  ·  Cross-validation  ·  Spot-futures correlation"
      >
        <ValidationDiagram />
      </StepCard>

      {/* ── Disk usage stacked bar ── */}
      <div
        style={{
          marginTop: 28,
          background: '#111820',
          border: '1px solid #1a2332',
          borderRadius: 12,
          padding: '20px 24px',
        }}
      >
        <div
          style={{
            fontSize: 17,
            fontWeight: 700,
            color: '#e8edf3',
            marginBottom: 14,
          }}
        >
          Storage{' '}
          <span style={{ fontSize: 14, color: '#5a6a7a', fontWeight: 400 }}>
            {fmtBytes(diskTotal)}
          </span>
        </div>
        {/* Stacked bar */}
        <div
          style={{
            height: 24,
            borderRadius: 8,
            overflow: 'hidden',
            display: 'flex',
            marginBottom: 14,
          }}
        >
          {Object.entries(summary.disk).map(([db, bytes]) => (
            <div
              key={db}
              style={{
                width: `${(bytes / diskTotal) * 100}%`,
                background: dbColors[db] ?? '#5a6a7a',
                height: '100%',
                minWidth: 2,
              }}
              title={`${db}: ${fmtBytes(bytes)}`}
            />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {Object.entries(summary.disk).map(([db, bytes]) => (
            <div
              key={db}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 3,
                  background: dbColors[db] ?? '#5a6a7a',
                }}
              />
              <span style={{ fontSize: 14, color: '#c5cdd8' }}>
                {db.replace('binance_', '')}
              </span>
              <span style={{ fontSize: 13, color: '#5a6a7a' }}>
                {fmtBytes(bytes)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main DeleteTab ─────────────────────────────────────────────────────────────
function DeleteTab(_props: DeleteTabProps) {
  const { disabledCoins } = useCoinRegistry();
  const [tab, setTab] = useState<'heatmap' | 'report'>('heatmap');
  const [market, setMarket] = useState<'spot' | 'futures'>('spot');
  const [summary, setSummary] = useState<SummaryInfo | null>(null);
  const [coins, setCoins] = useState<string[]>([]);

  // Heatmap filter state (lifted here so controls render in header)
  const [hmDb, setHmDb] = useState('binance_raw');
  const [hmDateFrom, setHmDateFrom] = useState(daysAgo(3));
  const [hmDateTo, setHmDateTo] = useState(todayStr());
  const [hmPreset, setHmPreset] = useState<string>('3d');
  const [hmInterval, setHmInterval] = useState('1h');

  const applyPreset = (preset: string) => {
    setHmPreset(preset);
    const today = todayStr();
    switch (preset) {
      case '1d':
        setHmDateFrom(daysAgo(1));
        setHmDateTo(today);
        break;
      case '3d':
        setHmDateFrom(daysAgo(3));
        setHmDateTo(today);
        break;
      case '7d':
        setHmDateFrom(daysAgo(7));
        setHmDateTo(today);
        break;
      case '14d':
        setHmDateFrom(daysAgo(14));
        setHmDateTo(today);
        break;
      case '30d':
        setHmDateFrom(daysAgo(30));
        setHmDateTo(today);
        break;
    }
  };

  // Inject scoped styles once
  useEffect(() => {
    const id = 'dc-scoped-styles';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id;
      el.textContent = DC_STYLES;
      document.head.appendChild(el);
    }
  }, []);

  // Load summary data directly from ClickHouse
  useEffect(() => {
    async function loadSummary() {
      const [
        spotRows,
        futuresRows,
        spot1mRows,
        futuresRows1m,
        diskRows,
        rangeRows,
        coinRows,
      ] = await Promise.all([
        chQuery<{ c: string }>(
          'SELECT count() AS c FROM binance_raw.spot_ohlcv_5m',
        ),
        chQuery<{ c: string }>(
          'SELECT count() AS c FROM binance_raw.futures_ohlcv_5m',
        ),
        chQuery<{ c: string }>(
          'SELECT count() AS c FROM binance_raw.spot_ohlcv_1m',
        ),
        chQuery<{ c: string }>(
          'SELECT count() AS c FROM binance_raw.futures_ohlcv_1m',
        ),
        chQuery<{ database: string; disk: string }>(`
          SELECT database, sum(bytes_on_disk) AS disk FROM system.parts
          WHERE database IN ('binance_raw','binance_cleaned','binance_spike','binance_preprocessed') AND active
          GROUP BY database ORDER BY database
        `),
        chQuery<{ mn: string; mx: string }>(
          'SELECT min(open_time) AS mn, max(open_time) AS mx FROM binance_raw.spot_ohlcv_5m',
        ),
        chQuery<{
          symbol: string;
          sc: string;
          fc: string;
          mn: string;
          mx: string;
        }>(`
          SELECT s.symbol, s.cnt AS sc, coalesce(f.cnt, 0) AS fc, s.min_t AS mn, s.max_t AS mx
          FROM (SELECT symbol, count() AS cnt, min(open_time) AS min_t, max(open_time) AS max_t FROM binance_raw.spot_ohlcv_5m FINAL GROUP BY symbol) s
          LEFT JOIN (SELECT symbol, count() AS cnt FROM binance_raw.futures_ohlcv_5m FINAL GROUP BY symbol) f ON s.symbol = f.symbol
          ORDER BY s.symbol
        `),
      ]);

      // Feature coverage
      const featureTables = [
        'open_interest',
        'global_ls_ratio',
        'top_ls_account',
        'top_ls_position',
        'taker_buy_sell',
        'funding_rate',
        'spot_depth',
        'futures_depth',
      ];
      const featureCoverageRows = await Promise.all(
        featureTables.map((ft) =>
          chQuery<{ symbol: string; cnt: string }>(
            `SELECT symbol, count() AS cnt FROM binance_raw.${ft} FINAL GROUP BY symbol`,
          ).then((rows) => rows.map((r) => ({ ...r, ft }))),
        ),
      );
      const featureCoverage: Record<string, Record<string, number>> = {};
      for (const row of featureCoverageRows.flat()) {
        if (!featureCoverage[row.symbol]) featureCoverage[row.symbol] = {};
        featureCoverage[row.symbol][row.ft] = parseInt(row.cnt);
      }

      // Preprocessed
      const ppTables = [
        'open_interest',
        'global_ls_ratio',
        'top_ls_account',
        'top_ls_position',
        'taker_buy_sell',
      ];
      const ppRows = await Promise.all(
        ppTables.map((ft) =>
          chQuery<{ symbol: string; cnt: string }>(
            `SELECT symbol, count() AS cnt FROM binance_preprocessed.${ft} FINAL GROUP BY symbol`,
          ).then((rows) => rows.map((r) => ({ ...r, ft }))),
        ),
      );
      const preprocessed: Record<string, Record<string, number>> = {};
      for (const row of ppRows.flat()) {
        if (!preprocessed[row.symbol]) preprocessed[row.symbol] = {};
        preprocessed[row.symbol][row.ft] = parseInt(row.cnt);
      }

      setSummary({
        spotRows: parseInt(spotRows[0]?.c ?? '0'),
        futuresRows: parseInt(futuresRows[0]?.c ?? '0'),
        spot1mRows: parseInt(spot1mRows[0]?.c ?? '0'),
        futures1mRows: parseInt(futuresRows1m[0]?.c ?? '0'),
        disk: Object.fromEntries(
          diskRows.map((r) => [r.database, parseInt(r.disk)]),
        ),
        minTime: rangeRows[0]?.mn ?? '',
        maxTime: rangeRows[0]?.mx ?? '',
        coins: coinRows.map((r) => ({
          symbol: r.symbol,
          spotRows: parseInt(r.sc),
          futuresRows: parseInt(r.fc),
          spotFrom: r.mn,
          spotTo: r.mx,
        })),
        featureCoverage,
        preprocessed,
      });

      setCoins(
        coinRows
          .map((r) => r.symbol)
          .filter((s) => !disabledCoins.has(s.replace(/USDT$/, ''))),
      );
    }
    loadSummary();
    const id = setInterval(loadSummary, 60_000);
    return () => clearInterval(id);
  }, []);

  const lastUpdate = summary?.maxTime?.slice(0, 16).replace('T', ' ') ?? '—';

  return (
    <div className="dc-wrap">
      <div className="dc-header">
        <div className="dc-header-left">
          <span className="dc-title">Live Monitor</span>
          <span className="dc-badge dc-badge-cyan">{coins.length} symbols</span>
          <span className="dc-badge dc-badge-green">{hmInterval}</span>
          <span className="dc-badge dc-badge-yellow">Binance</span>
          <div className="dc-tab-bar" style={{ marginLeft: 12 }}>
            <button
              className={`dc-tab-btn${tab === 'heatmap' ? ' dc-active' : ''}`}
              onClick={() => setTab('heatmap')}
            >
              Heatmap
            </button>
            <button
              className={`dc-tab-btn${tab === 'report' ? ' dc-active' : ''}`}
              onClick={() => setTab('report')}
            >
              Report
            </button>
          </div>
        </div>
        <div className="dc-header-right">
          <span style={{ color: '#5a6a7a', fontSize: 10 }}>
            Last: {lastUpdate} UTC
          </span>
          {tab === 'heatmap' && (
            <>
              <div className="dc-filter-sep" />
              <span className="dc-ctrl-label">DB:</span>
              <div className="dc-ctrl-group">
                {DB_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    className={`dc-ctrl-btn${hmDb === o.value ? ' dc-active' : ''}`}
                    onClick={() => setHmDb(o.value)}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <div className="dc-filter-sep" />
              <div className="dc-ctrl-group">
                {['1d', '3d', '7d', '14d', '30d'].map((p) => (
                  <button
                    key={p}
                    className={`dc-ctrl-btn${hmPreset === p ? ' dc-active' : ''}`}
                    onClick={() => applyPreset(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <div className="dc-filter-sep" />
              <DateRangeCal
                from={hmDateFrom}
                to={hmDateTo}
                onChange={(f, t) => {
                  setHmDateFrom(f);
                  setHmDateTo(t);
                  setHmPreset('');
                }}
              />
              <div className="dc-filter-sep" />
              <span className="dc-ctrl-label">Interval:</span>
              <div className="dc-ctrl-group">
                {INTERVAL_OPTIONS.map((iv) => (
                  <button
                    key={iv}
                    className={`dc-ctrl-btn${hmInterval === iv ? ' dc-active' : ''}`}
                    onClick={() => setHmInterval(iv)}
                  >
                    {iv}
                  </button>
                ))}
              </div>
            </>
          )}
          <div className="dc-filter-sep" />
          <span className="dc-ctrl-label">Market:</span>
          <div className="dc-ctrl-group">
            <button
              className={`dc-ctrl-btn${market === 'futures' ? ' dc-active' : ''}`}
              onClick={() => setMarket('futures')}
            >
              SWAP
            </button>
            <button
              className={`dc-ctrl-btn${market === 'spot' ? ' dc-active' : ''}`}
              onClick={() => setMarket('spot')}
            >
              SPOT
            </button>
          </div>
        </div>
      </div>

      <div className="dc-main">
        <div style={{ display: tab === 'heatmap' ? undefined : 'none' }}>
          <HeatmapPanel
            summary={summary}
            market={market}
            db={hmDb}
            dateFrom={hmDateFrom}
            dateTo={hmDateTo}
            interval={hmInterval}
          />
        </div>
        {tab === 'report' && <ReportPanel summary={summary} />}
      </div>
    </div>
  );
}

export default DeleteTab;
